import { Card, Group, Skeleton, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../shared/components/EmptyState";
import { StatusBadge } from "../../../shared/components/StatusBadge";
import { formatDateTime } from "../../../shared/utils/format-date";
import { resolveLabel } from "../../../shared/utils/i18n";
import type { WelcomeRequest } from "./WelcomeDashboard";

interface WelcomeRequestFeedProps {
  requests: WelcomeRequest[];
  viewAllHref?: string;
  /** Base path for request detail links, without trailing slash.
   *  Defaults to stripping query params from viewAllHref. */
  requestsBasePath?: string;
  isLoading?: boolean;
  maxItems?: number;
}

export function WelcomeRequestFeed({
  requests,
  viewAllHref,
  requestsBasePath,
  isLoading = false,
  maxItems = 5,
}: WelcomeRequestFeedProps) {
  const basePath =
    requestsBasePath ?? viewAllHref?.split("?").at(0)?.replace(/\/$/, "") ?? "";
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (isLoading) {
    return (
      <Stack gap="xs">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} height={52} radius="xs" />
        ))}
      </Stack>
    );
  }

  if (requests.length === 0) {
    return <EmptyState title={t("welcome.noRequests", "No open requests")} />;
  }

  return (
    <Stack gap="xs">
      {requests.slice(0, maxItems).map((req) => (
        <Card
          key={req.id}
          component="a"
          href={`${basePath}/${req.id}`}
          p="sm"
          td="none"
          aria-label={`${req.referenceNo} — ${resolveLabel(req.documentTypeName, req.documentTypeTranslations, locale)}`}
          data-testid={`welcome-request-${req.id}`}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Text size="sm" fw={500}>
                {req.referenceNo}
              </Text>
              <StatusBadge
                label={resolveLabel(
                  req.documentTypeName,
                  req.documentTypeTranslations,
                  locale,
                )}
                variant="type"
              />
            </Group>
            <Group gap="sm" wrap="nowrap">
              <StatusBadge
                label={resolveLabel(
                  req.currentState.name,
                  req.currentState.translations,
                  locale,
                )}
                isFinal={req.currentState.isFinal}
              />
              <Text
                size="xs"
                c="dimmed"
                visibleFrom="sm"
                data-mask-vrt
                data-testid={`welcome-request-timestamp-${req.id}`}
              >
                {formatDateTime(req.updatedAt)}
              </Text>
            </Group>
          </Group>
        </Card>
      ))}
      {viewAllHref && (
        <Text
          size="sm"
          component="a"
          href={viewAllHref}
          c="dimmed"
          ta="right"
          td="none"
          data-testid="welcome-view-all-requests"
        >
          {t("welcome.viewAll")} <span aria-hidden="true">→</span>
        </Text>
      )}
    </Stack>
  );
}
